import React, { useState, useEffect } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Button } from 'src/components/ui/button';
import { AlertTriangle, RefreshCw, Wifi } from 'lucide-react';
import * as Sentry from '@sentry/react';
import ENV from 'src/env.json';
import { handleChunkError, isChunkLoadError } from 'src/utils/chunk-error-handler';

const GlobalErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const [isRecovering, setIsRecovering] = useState(false);
  const chunkError = isChunkLoadError(error);

  // Auto-attempt recovery for chunk errors
  useEffect(() => {
    if (chunkError && !isRecovering) {
      setIsRecovering(true);
      handleChunkError(error).then((recovered) => {
        if (!recovered) {
          // Recovery failed (already reloaded recently), show fallback UI
          setIsRecovering(false);
        }
        // If recovered === true, page is reloading — no action needed
      });
    }
  }, [chunkError, error, isRecovering]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleHardReload = async () => {
    // Clear all caches and force reload
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }
    } catch { /* ignore */ }
    window.location.reload();
  };

  const handleReset = () => {
    resetErrorBoundary();
  };

  // Show loading state while auto-recovering from chunk error
  if (isRecovering) {
    return (
      <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-p-4">
        <div className="tw-max-w-md tw-w-full tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-text-center">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <RefreshCw className="tw-h-16 tw-w-16 tw-text-blue-500 tw-animate-spin" />
          </div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
            Updating application...
          </h1>
          <p className="tw-text-gray-600">
            A new version is available. Please wait while we refresh.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-p-4">
      <div className="tw-max-w-md tw-w-full tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-text-center">
        <div className="tw-flex tw-justify-center tw-mb-4">
          {chunkError ? (
            <Wifi className="tw-h-16 tw-w-16 tw-text-orange-500" />
          ) : (
            <AlertTriangle className="tw-h-16 tw-w-16 tw-text-red-500" />
          )}
        </div>
        <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
          {chunkError ? 'App Update Required' : 'Something went wrong'}
        </h1>
        <p className="tw-text-gray-600 tw-mb-6">
          {chunkError
            ? 'A new version has been deployed. Please clear your cache and reload to get the latest version.'
            : 'We apologize for the inconvenience. An unexpected error has occurred.'}
        </p>
        {ENV.NODE_ENV === 'dev' && error?.message && (
          <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-mb-6 tw-text-left">
            <p className="tw-text-sm tw-font-mono tw-text-red-800 tw-break-words">
              {error.message}
            </p>
          </div>
        )}
        <div className="tw-flex tw-flex-col tw-gap-3">
          {chunkError ? (
            <Button onClick={handleHardReload} className="tw-w-full">
              <RefreshCw className="tw-h-4 tw-w-4 tw-mr-2" />
              Clear Cache & Reload
            </Button>
          ) : (
            <>
              <Button onClick={handleReload} className="tw-w-full">
                <RefreshCw className="tw-h-4 tw-w-4 tw-mr-2" />
                Reload Page
              </Button>
              <Button onClick={handleReset} variant="outline" className="tw-w-full">
                Try Again
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const reportToSentry = (error: Error, info: React.ErrorInfo) => {
  // Log to console for development
  console.error('[GlobalErrorBoundary] Uncaught error:', error);
  console.error('[GlobalErrorBoundary] Component stack:', info.componentStack);

  // Report to Sentry in non-dev environments
  if (ENV.NODE_ENV !== 'dev' && ENV.SENTRY_DSN) {
    Sentry.captureException(error, {
      tags: {
        errorBoundary: 'GlobalErrorBoundary',
      },
      extra: {
        componentStack: info.componentStack,
      },
    });
  }
};

type GlobalErrorBoundaryProps = {
  children: React.ReactNode;
};

const GlobalErrorBoundary: React.FC<GlobalErrorBoundaryProps> = ({ children }) => {
  // Use key to force remount on reset
  const [resetKey, setResetKey] = useState(0);

  const handleReset = () => {
    // Increment key to force full remount of children
    setResetKey((prev) => prev + 1);
  };

  return (
    <ErrorBoundary
      key={resetKey}
      FallbackComponent={GlobalErrorFallback}
      onError={reportToSentry}
      onReset={handleReset}
    >
      {children}
    </ErrorBoundary>
  );
};

export { GlobalErrorBoundary };
